import { useState, useEffect, useMemo, memo, useCallback, useRef } from 'react';
import {
  Box,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  ListSubheader,
  Avatar,
  Chip,
  Typography,
  CircularProgress,
  Divider,
  Alert,
  InputAdornment,
  Paper,
  ClickAwayListener,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  PersonOff as OfflinePlayerIcon,
  PersonAdd as ConnectionIcon,
  Schedule as PendingIcon,
  Block as BlockIcon,
  EventSeat as ReservedIcon,
  Clear as ClearIcon,
  CheckCircle as OnBoardIcon,
} from '@mui/icons-material';
import type { AssignablePlayer, AssignablePlayersResult } from '../../types';
import { boardsApi } from '../../api';
import { renderIcon } from '../IconPicker';

export interface AssignModeDropdownProps {
  boardId: string;
  selectedPlayer: AssignModePlayerSelection | null;
  onPlayerSelect: (player: AssignModePlayerSelection | null) => void;
  squaresPerCell?: number;
  disabled?: boolean;
}

// Explicit player type for clearer type safety
export type AssignModePlayerType = 'user' | 'offline_player';

export interface AssignModePlayerSelection {
  id: string;
  playerType: AssignModePlayerType; // Explicit type for clarity
  displayName: string;
  /** @deprecated Use playerType === 'offline_player' instead */
  isOfflinePlayer: boolean;
  isPending?: boolean;
  approvedCells?: number;
  claimedCells?: number;
  reservedCount?: number;
}

// Helper to get initials
const getInitials = (name?: string): string => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

export const AssignModeDropdown = memo<AssignModeDropdownProps>(({
  boardId,
  selectedPlayer,
  onPlayerSelect,
  squaresPerCell = 1,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AssignablePlayersResult | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const listRef = useRef<HTMLUListElement>(null);

  const fetchAssignablePlayers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await boardsApi.getAssignablePlayers(boardId);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load players');
    } finally {
      setLoading(false);
    }
  }, [boardId]);

  // Fetch assignable players when dropdown opens (always refetch for fresh data)
  useEffect(() => {
    if (open) {
      fetchAssignablePlayers();
    }
  }, [open, fetchAssignablePlayers]);

  // Filter players by search query
  const filteredData = useMemo(() => {
    if (!data) return null;
    if (!searchQuery.trim()) return data;

    const query = searchQuery.toLowerCase().trim();
    const filterPlayer = (p: AssignablePlayer): boolean => {
      const name = p.user?.displayName || p.offlinePlayer?.displayName || p.inviteEmail || '';
      const handle = p.user?.handle || p.offlinePlayer?.handle || '';
      return name.toLowerCase().includes(query) || handle.toLowerCase().includes(query);
    };

    return {
      onBoard: data.onBoard.filter(filterPlayer),
      connections: data.connections.filter(filterPlayer),
      offlinePlayers: data.offlinePlayers.filter(filterPlayer),
      pendingInvites: data.pendingInvites.filter(filterPlayer),
    };
  }, [data, searchQuery]);

  // Flatten selectable players for keyboard navigation (exclude pending invites)
  const selectablePlayers = useMemo(() => {
    if (!filteredData) return [];
    return [
      ...filteredData.onBoard,
      ...filteredData.connections,
      ...filteredData.offlinePlayers,
    ].filter(p => !p.isPending);
  }, [filteredData]);

  // Reset highlighted index when dropdown opens/closes or search changes
  useEffect(() => {
    setHighlightedIndex(open && selectablePlayers.length > 0 ? 0 : -1);
  }, [open, selectablePlayers.length]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('[data-player-item]');
      const item = items[highlightedIndex] as HTMLElement | undefined;
      if (item?.scrollIntoView) {
        item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [highlightedIndex]);

  // Convert AssignablePlayer to selection format
  const toSelection = useCallback((player: AssignablePlayer): AssignModePlayerSelection => {
    const isOffline = player.type === 'board_offline_player' || player.type === 'offline_player';
    return {
      id: player.userId || player.offlinePlayerId || '',
      playerType: isOffline ? 'offline_player' : 'user',
      displayName: player.user?.displayName || player.offlinePlayer?.displayName || player.inviteEmail || 'Unknown',
      isOfflinePlayer: isOffline, // Kept for backwards compatibility
      isPending: player.isPending,
      approvedCells: player.approvedSquares,
      claimedCells: player.claimedSquares,
      reservedCount: player.reservedCount,
    };
  }, []);

  const handlePlayerClick = useCallback((player: AssignablePlayer) => {
    // Don't allow selecting pending invites
    if (player.isPending) return;

    const selection = toSelection(player);
    onPlayerSelect(selection);
    setOpen(false);
    setSearchQuery('');
  }, [toSelection, onPlayerSelect]);

  // Keyboard navigation handler
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!open) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < selectablePlayers.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : selectablePlayers.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < selectablePlayers.length) {
          handlePlayerClick(selectablePlayers[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        setSearchQuery('');
        break;
    }
  }, [open, highlightedIndex, selectablePlayers, handlePlayerClick]);

  const handleClearSelection = () => {
    onPlayerSelect(null);
  };

  const renderPlayerItem = (player: AssignablePlayer, section: 'onBoard' | 'connections' | 'offlinePlayers' | 'pendingInvites', flatIndex?: number) => {
    const displayName = player.user?.displayName || player.offlinePlayer?.displayName || player.inviteEmail || 'Unknown';
    const handle = player.user?.handle || player.offlinePlayer?.handle;
    const iconName = player.user?.iconName || player.offlinePlayer?.iconName;
    const isOnBoard = player.isOnBoard;
    const isPending = player.isPending;
    const claimedCells = Math.round((player.claimedSquares || 0) / squaresPerCell);
    const approvedCells = player.approvedSquares || 0;
    const reservedCount = player.reservedCount || 0;
    const isSelected = selectedPlayer?.id === (player.userId || player.offlinePlayerId);
    const isHighlighted = flatIndex !== undefined && flatIndex === highlightedIndex;

    // Determine avatar color and icon
    let avatarBgColor = 'grey.400';
    let avatarIcon: React.ReactNode = null;
    if (player.type === 'board_participant') {
      avatarBgColor = 'primary.main';
      avatarIcon = iconName ? renderIcon(iconName, 24) : null;
    } else if (player.type === 'board_offline_player' || player.type === 'offline_player') {
      avatarBgColor = 'grey.600';
      avatarIcon = iconName ? renderIcon(iconName, 24) : <OfflinePlayerIcon />;
    } else if (player.type === 'connection') {
      avatarBgColor = 'info.main';
      avatarIcon = iconName ? renderIcon(iconName, 24) : <ConnectionIcon />;
    } else if (player.type === 'pending_invite') {
      avatarBgColor = 'warning.main';
      avatarIcon = <PendingIcon />;
    }

    return (
      <ListItem
        key={player.userId || player.offlinePlayerId || player.inviteId || player.inviteEmail}
        component="li"
        data-player-item={flatIndex !== undefined ? 'true' : undefined}
        onClick={() => !isPending && handlePlayerClick(player)}
        sx={{
          cursor: isPending ? 'not-allowed' : 'pointer',
          opacity: isPending ? 0.6 : 1,
          bgcolor: isHighlighted ? 'action.focus' : isSelected ? 'action.selected' : 'transparent',
          outline: isHighlighted ? '2px solid' : 'none',
          outlineColor: 'primary.main',
          '&:hover': {
            bgcolor: isPending ? 'transparent' : 'action.hover',
          },
          borderRadius: 1,
          mx: 1,
          my: 0.5,
        }}
      >
        <ListItemAvatar>
          <Avatar
            sx={{
              bgcolor: avatarBgColor,
              width: 40,
              height: 40,
            }}
          >
            {avatarIcon || getInitials(displayName)}
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" component="span">
                {displayName}
              </Typography>
              {handle && (
                <Typography variant="caption" color="text.secondary" component="span">
                  @{handle.toUpperCase()}
                </Typography>
              )}
              {isOnBoard && (
                <Tooltip title="On board">
                  <OnBoardIcon sx={{ fontSize: 16, color: 'success.main' }} />
                </Tooltip>
              )}
              {isPending && (
                <Chip
                  label="Pending"
                  size="small"
                  color="warning"
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
              )}
              {reservedCount > 0 && (
                <Tooltip title={`${reservedCount} reserved squares`}>
                  <Chip
                    icon={<ReservedIcon sx={{ fontSize: 12 }} />}
                    label={reservedCount}
                    size="small"
                    color="info"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.65rem' }}
                  />
                </Tooltip>
              )}
            </Box>
          }
          secondary={
            isOnBoard && approvedCells > 0 ? (
              <Typography variant="caption" color="text.secondary">
                {claimedCells}/{approvedCells} {squaresPerCell > 1 ? 'cells' : 'squares'}
              </Typography>
            ) : section !== 'onBoard' ? (
              <Typography variant="caption" color="text.secondary">
                {section === 'connections' ? 'Connection' : section === 'offlinePlayers' ? 'Offline player' : 'Invited'}
              </Typography>
            ) : null
          }
        />
        <ListItemSecondaryAction>
          {isPending && (
            <Tooltip title="Cannot assign to pending invites">
              <BlockIcon sx={{ fontSize: 18, color: 'warning.main' }} />
            </Tooltip>
          )}
        </ListItemSecondaryAction>
      </ListItem>
    );
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ position: 'relative' }}>
        {/* Selected player display or search field */}
        {selectedPlayer ? (
          <Paper
            variant="outlined"
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 1,
              cursor: disabled ? 'default' : 'pointer',
            }}
            onClick={() => !disabled && setOpen(true)}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                mr: 1,
                bgcolor: selectedPlayer.isOfflinePlayer ? 'grey.600' : 'primary.main',
                fontSize: '0.8rem',
              }}
            >
              {getInitials(selectedPlayer.displayName)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {selectedPlayer.displayName}
              </Typography>
              {(selectedPlayer.approvedCells !== undefined && selectedPlayer.approvedCells > 0) && (
                <Typography variant="caption" color="text.secondary">
                  {selectedPlayer.claimedCells || 0}/{selectedPlayer.approvedCells} {squaresPerCell > 1 ? 'cells' : 'squares'}
                </Typography>
              )}
            </Box>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleClearSelection();
              }}
              disabled={disabled}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </Paper>
        ) : (
          <TextField
            fullWidth
            size="small"
            placeholder="Select a player to assign squares..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        )}

        {/* Dropdown list */}
        {open && (
          <Paper
            elevation={8}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 1300,
              maxHeight: 400,
              overflow: 'auto',
              mt: 0.5,
            }}
          >
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ m: 1 }}>
                {error}
              </Alert>
            )}

            {filteredData && !loading && (
              <List dense disablePadding ref={listRef}>
                {/* Players on board section */}
                {filteredData.onBoard.length > 0 && (
                  <>
                    <ListSubheader
                      sx={{
                        bgcolor: 'grey.100',
                        lineHeight: '32px',
                        fontWeight: 'bold',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <OnBoardIcon fontSize="small" color="success" />
                        On Board ({filteredData.onBoard.length})
                      </Box>
                    </ListSubheader>
                    {filteredData.onBoard.filter(p => !p.isPending).map((player, idx) => renderPlayerItem(player, 'onBoard', idx))}
                  </>
                )}

                {/* Connections section */}
                {filteredData.connections.length > 0 && (
                  <>
                    {filteredData.onBoard.length > 0 && <Divider />}
                    <ListSubheader
                      sx={{
                        bgcolor: 'grey.100',
                        lineHeight: '32px',
                        fontWeight: 'bold',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ConnectionIcon fontSize="small" color="info" />
                        Your Connections ({filteredData.connections.length})
                      </Box>
                    </ListSubheader>
                    {filteredData.connections.filter(p => !p.isPending).map((player, idx) => {
                      const baseIndex = filteredData.onBoard.filter(p => !p.isPending).length;
                      return renderPlayerItem(player, 'connections', baseIndex + idx);
                    })}
                  </>
                )}

                {/* Offline players not on board section */}
                {filteredData.offlinePlayers.length > 0 && (
                  <>
                    {(filteredData.onBoard.length > 0 || filteredData.connections.length > 0) && <Divider />}
                    <ListSubheader
                      sx={{
                        bgcolor: 'grey.100',
                        lineHeight: '32px',
                        fontWeight: 'bold',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <OfflinePlayerIcon fontSize="small" />
                        Your Offline Players ({filteredData.offlinePlayers.length})
                      </Box>
                    </ListSubheader>
                    {filteredData.offlinePlayers.filter(p => !p.isPending).map((player, idx) => {
                      const baseIndex = filteredData.onBoard.filter(p => !p.isPending).length +
                                        filteredData.connections.filter(p => !p.isPending).length;
                      return renderPlayerItem(player, 'offlinePlayers', baseIndex + idx);
                    })}
                  </>
                )}

                {/* Pending invites section - no keyboard navigation for pending */}
                {filteredData.pendingInvites.length > 0 && (
                  <>
                    {(filteredData.onBoard.length > 0 || filteredData.connections.length > 0 || filteredData.offlinePlayers.length > 0) && <Divider />}
                    <ListSubheader
                      sx={{
                        bgcolor: 'warning.50',
                        lineHeight: '32px',
                        fontWeight: 'bold',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PendingIcon fontSize="small" color="warning" />
                        Pending Invitations ({filteredData.pendingInvites.length})
                      </Box>
                    </ListSubheader>
                    {filteredData.pendingInvites.map((player) => renderPlayerItem(player, 'pendingInvites'))}
                  </>
                )}

                {/* No results */}
                {filteredData.onBoard.length === 0 &&
                  filteredData.connections.length === 0 &&
                  filteredData.offlinePlayers.length === 0 &&
                  filteredData.pendingInvites.length === 0 && (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {searchQuery ? 'No players match your search' : 'No players available'}
                    </Typography>
                  </Box>
                )}
              </List>
            )}
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
});

AssignModeDropdown.displayName = 'AssignModeDropdown';

export default AssignModeDropdown;
